import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Canvas as CanvasType } from 'fabric';

// Types
import { OpticalAnswer } from '../../../types';

// Stores
import { useWhiteboardStore } from '../../../store/useWhiteboardStore';
import { useTestStore } from '../../../store/useTestStore';
import { useAuthStore } from '../../../store/useAuthStore';
import { useUserStore } from '../../../store/useUserStore';
import { useTenantStore } from '../../../store/useTenantStore';
import { useTestMetadataStore } from '../../../store/useTestMetadataStore';

// Hooks
import { useTestLoader } from '../../../hooks/useTestLoader';
import { useQuestionStorage } from '../../../hooks/useQuestionStorage';
import { useAnswerData } from '../../../hooks/useAnswerData';
import { useTestSession } from '../../../hooks/useTestSession';

// Services
import { 
  DynamicFirebaseService, 
  createExternalFirebaseService, 
  getDefaultFirebaseService 
} from '../../../lib/firebase/dynamicFirebaseService';
import { DynamicQuestionStorageService } from '../../../lib/firebase/questionStorageServiceDynamic';
import { initializeFirebaseAuth } from '../../../lib/firebase/authService';
import { ExternalDataService } from '../../../services/dataService';

// Types
import { QBTestQuestionEto } from '../../../lib/api/types';
import { WhiteboardExternalProps } from '../../../types/whiteboardProps';
import { QuestionData } from '../../../types/questionData';

// Utils for drawing
import { Path } from '../../../lib/fabricLoader';
import { getPointsToSmoothPath } from '../../../utils/drawing';
import { buildUIConfigFromTest } from '../../../utils/testUIConfigBuilder';
import { cleanupTestData, cleanupWebViewTestData } from '../../../utils/testCleanup';

// Constants for blank whiteboard
const BLANK_WHITEBOARD_TEST_ID = 'blank-whiteboard-00000000-0000-0000-0000-000000000000';
const BLANK_WHITEBOARD_QUESTION_ID = 'blank-question-00000000-0000-0000-0000-000000000000';

interface WhiteboardContextType {
  // Test data
  qbTestId: string | null;
  qbQuestions: QBTestQuestionEto[];
  qbGroups: any[];
  currentQuestionIndex: number;
  currentQbQuestion: QBTestQuestionEto | null;
  testTitle: string;
  totalTime: number | undefined;
  isTestMode: boolean;
  isTestCompleted: boolean;
  
  // Optical answers
  opticalAnswers: Record<string, OpticalAnswer>;
  eliminatedChoices: Record<string, Set<string>>;
  
  // Canvas state
  fabricCanvasRef: React.MutableRefObject<CanvasType | null>;
  canvasReady: boolean;
  setCanvasReady: (ready: boolean) => void;
  
  // Loading states
  isLoadingTest: boolean;
  isLoadingQuestions: boolean;
  error: string | null;
  webViewLoadingError: boolean;
  
  // Firebase
  firebaseReady: boolean;
  firebaseService: DynamicFirebaseService | null;
  questionStorageService: DynamicQuestionStorageService | null;
  
  // Modes
  isOpticalMode: boolean;
  isWebViewMode: boolean;
  showOpticalPanelState: boolean;
  
  // Recording
  isRecording: boolean;
  recordingEvents: any[];
  
  // Session
  sessionCount: number;
  previousSessionsTime: number;
  
  // Actions - Navigation
  navigateToQuestion: (index: number) => void;
  goToNextQuestion: () => void;
  goToPreviousQuestion: () => void;
  setSelectedGroupIndex: (index: number | null) => void;
  
  // Actions - Test management
  loadTest: (testId: string) => Promise<void>;
  handleTestComplete: () => Promise<void>;
  restoreTestState: (testId: string, questions: QBTestQuestionEto[], studyTaskId?: string) => Promise<number>;

  // Actions - Question data
  saveQuestionData: () => Promise<void>;
  loadQuestionData: () => Promise<any>;
  syncToFirebase: () => Promise<void>;
  
  // Actions - Optical answers
  submitOpticalAnswer: (choice: 'A' | 'B' | 'C' | 'D' | 'E' | '') => void;
  eliminateChoice: (choice: string) => void;
  clearAnswer: (questionId: string) => void;
  clearAllAnswers: () => void;
  
  // Actions - UI
  setShowOpticalPanelState: (show: boolean) => void;
  
  // Answer data service methods
  saveAnswer: (questionId: string, answer: 'A' | 'B' | 'C' | 'D' | 'E') => Promise<void>;
  savePendingOpticalAnswers: () => Promise<void>;
  submitBulkAnswers: () => Promise<void>;
  markQuestionSkipped: (questionId: string) => Promise<void>;
  
}

const WhiteboardContext = createContext<WhiteboardContextType | null>(null);

export const useWhiteboard = () => {
  const context = useContext(WhiteboardContext);
  if (!context) {
    throw new Error('useWhiteboard must be used within WhiteboardProvider');
  }
  return context;
};

interface WhiteboardProviderProps {
  children: React.ReactNode;
  externalProps?: WhiteboardExternalProps;
}

export const WhiteboardProvider: React.FC<WhiteboardProviderProps> = ({ 
  children, 
  externalProps 
}) => {
  
  // Route params
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qbTestIdFromUrl = params.qbTestId || searchParams.get('qbTestId') || null;
  const qbTestId = externalProps?.testId || qbTestIdFromUrl;
  
  // Log only in development
  // console.log('🎨 WhiteboardProvider - Route params:', {
  //   params,
  //   qbTestIdFromUrl,
  //   qbTestId,
  //   searchParams: Object.fromEntries(searchParams.entries())
  // });

  // Store hooks
  const { accessToken } = useAuthStore();
  const { userInfo } = useUserStore();
  const { tenantId } = useTenantStore();
  const tenantInfo = undefined; // TODO: Add tenantInfo to TenantStore when needed
  const { setTestMetadata } = useTestMetadataStore();
  const {
    strokes,
    clearStrokes,
    setCurrentTestId,
    setCurrentQuestionId,
    setCurrentQuestion,
    loadDevicePreferences,
    setStorageService,
    questionDataCache,
    setQuestionDataCache
  } = useWhiteboardStore();
  
  // Test store
  const {
    currentTestId,
    questions: testQuestions,
    groups: testGroups,
    currentQuestionIndex,
    selectedGroupIndex,
    testTitle,
    totalTime,
    isTestMode,
    isTestCompleted,
    opticalAnswers,
    eliminatedChoices: eliminatedChoicesString,
    setQuestions,
    setGroups,
    setTestTitle,
    setTotalTime,
    setTestCompleted,
    setCurrentQuestionIndex,
    setSelectedGroupIndex,
    setOpticalAnswer,
    setOpticalAnswers,
    toggleEliminatedChoice,
    clearAnswers,
    goToNextQuestion: storeGoToNext,
    goToPreviousQuestion: storeGoToPrevious
  } = useTestStore();

  // State
  const [canvasReady, setCanvasReady] = useState(false);
  const [isLoadingTest, setIsLoadingTest] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [firebaseService, setFirebaseService] = useState<DynamicFirebaseService | null>(null);
  const [questionStorageService, setQuestionStorageService] = useState<DynamicQuestionStorageService | null>(null);
  const [isInitializingFirebase, setIsInitializingFirebase] = useState(false);
  const [showOpticalPanelState, setShowOpticalPanelState] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingEvents, setRecordingEvents] = useState<any[]>([]);
  const [studyTaskId, setStudyTaskId] = useState<string | null>(null);
  const [firebaseAuthReady, setFirebaseAuthReady] = useState(false);
  const [webViewLoadingError, setWebViewLoadingError] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);



  // Refs
  const fabricCanvasRef = useRef<CanvasType | null>(null);
  const saveCurrentQuestionDataRef = useRef<(() => Promise<void>) | null>(null);
  const questionStartTimeRef = useRef<number | null>(null);
  const currentQuestionIdRef = useRef<string | null>(null);

  // Convert eliminated choices from string to Set
  const eliminatedChoices = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    Object.entries(eliminatedChoicesString).forEach(([questionId, choicesStr]) => {
      result[questionId] = new Set(choicesStr.split(''));
    });
    return result;
  }, [eliminatedChoicesString]);

  // Modes
  // URL parameter has priority over external props
  const urlOptikParam = searchParams.get('optik');
  const isOpticalMode = urlOptikParam !== null 
    ? urlOptikParam === '1'
    : (externalProps?.uiConfig?.showOpticalForm || false);
  const isWebViewMode = window.parent !== window || !!(window as any).ReactNativeWebView;
  const isAuthenticated = useMemo(() => !!accessToken || !!externalProps, [accessToken, externalProps]);
  
  
  // Set optical panel visibility based on mode - only run once
  useEffect(() => {
    if (isOpticalMode) {
      setShowOpticalPanelState(true);
    }
  }, []); // Empty deps to run only once

  // Current question - memoized to prevent unnecessary re-renders
  const currentQbQuestion = useMemo(() => {
    return testQuestions[currentQuestionIndex] || null;
  }, [testQuestions, currentQuestionIndex]);
  
  // Update store when current question changes
  useEffect(() => {
    const updateQuestionInStore = async () => {
      if (currentQbQuestion) {
        const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
        const questionId = getStandardQuestionId(currentQbQuestion);
        
        if (questionId) {
       
          setCurrentQuestionId(questionId);
          setCurrentQuestion(currentQbQuestion);
        } else {
        
          setCurrentQuestionId(null);
          setCurrentQuestion(null);
        }
      } else {
        setCurrentQuestionId(null);
        setCurrentQuestion(null);
      }
    };
    
    updateQuestionInStore();
  }, [currentQbQuestion, setCurrentQuestionId, setCurrentQuestion]);
  

  // Initialize test loader
  const {
    loadTest: loadTestData,
    loadQuestions: loadQuestionsData,
    isLoading: isLoadingQuestions
  } = useTestLoader({
    accessToken,
    tenantId,
    externalProps
  });

  // Initialize question storage
  const {
    saveQuestionData: saveQuestionDataHook,
    loadQuestionData: loadQuestionDataHook,
    syncToFirebase: syncToFirebaseHook
  } = useQuestionStorage({
    userInfo,
    tenantId,
    qbTestId,
    isWebViewMode
  });

  // Initialize test session
  const {
    saveTestSession,
    loadPreviousSession,
    handleTestComplete: handleTestCompleteSession,
    sessionCount,
    previousSessionsTime
  } = useTestSession({
    userInfo,
    tenantId,
    qbTestId,
    qbQuestions: testQuestions,
    currentQuestionIndex,
    strokes,
    fabricCanvasRef,
    totalTestTime: totalTime,
    isWebViewMode
  });

  // Initialize answer data service
  const {
    isInitialized: isAnswerDataInitialized,
    isSyncing,
    syncError,
    syncStatus,
    saveAnswer,
    savePendingOpticalAnswers,
    submitBulkAnswers,
    getSyncStatus,
    markQuestionSkipped
  } = useAnswerData({
    testId: qbTestId || '',
    studyTaskId: studyTaskId || qbTestId || '',
    testTitle,
    totalQuestions: testQuestions.length,
    bulkSubmitUrl: externalProps?.testConfig?.studyTask?.bulkSubmitUrl,
    isExternalMode: window.location.pathname === '/webview',
    userId: userInfo?.id || externalProps?.authConfig?.userInfo?.id,
    tenantId: tenantId || externalProps?.authConfig?.tenantId,
    onSyncStatusChange: externalProps?.callbacks?.onSyncStatusChange,
    onBulkSubmitSuccess: externalProps?.callbacks?.onBulkSubmitSuccess,
    onBulkSubmitError: externalProps?.callbacks?.onBulkSubmitError
  });
  
  // Periodically save resume data (every 30 seconds)
  useEffect(() => {
    if (!qbTestId || !currentQbQuestion || externalProps) return;
    
    const saveResumeData = () => {
      const currentTime = questionStartTimeRef.current ?
        Date.now() - questionStartTimeRef.current : 0;
      const totalUsedTime = previousSessionsTime + currentTime;

      // Get current question index from store
      const currentIndex = useTestStore.getState().currentQuestionIndex;

      const resumeData = {
        lastActiveQuestionId: currentQbQuestion.questionId,
        lastActiveQuestionIndex: currentIndex,
        usedTime: totalUsedTime,
        timestamp: Date.now()
      };
      
      // Save to localStorage only - no need for periodic Firebase updates
      localStorage.setItem(`test_resume_${qbTestId}`, JSON.stringify(resumeData));
    };
    
    // Save immediately
    saveResumeData();
    
    // Then save every 30 seconds
    const interval = setInterval(saveResumeData, 30000);
    
    return () => clearInterval(interval);
  }, [qbTestId, currentQbQuestion, userInfo?.id, tenantId, previousSessionsTime, externalProps]);

  // Initialize Firebase services
  useEffect(() => {
    console.log('🔥 [Firebase Init Effect] Triggered:', {
      hasExternalProps: !!externalProps,
      hasFirebaseConfig: !!externalProps?.firebaseConfig
    });

    const initFirebase = async () => {
      setIsInitializingFirebase(true);
      try {
        let service: DynamicFirebaseService;

        if (externalProps?.firebaseConfig) {
          console.log('🔥 [Firebase Init] Using external Firebase config');
          // Use external Firebase config
          service = createExternalFirebaseService(externalProps.firebaseConfig);
          
          // Authenticate if token API is provided
          if (externalProps.firebaseConfig.tokenApiRoot || externalProps.firebaseConfig.getFirebaseToken) {
            try {
              await service.authenticate();
          // console.log('✅ External Firebase authenticated successfully');
            } catch (authError) {
              console.error('❌ External Firebase authentication failed:', authError);
              // Continue without Firebase - questions will still work but no answer saving
            }
          }
        } else {
          console.log('🔥 [Firebase Init] Using default Firebase service');
          // Use default Firebase
          service = getDefaultFirebaseService();
        }

        console.log('🔥 [Firebase Init] Setting storage service');
        setFirebaseService(service);
        const storageService = new DynamicQuestionStorageService(service);
        setQuestionStorageService(storageService);
        setStorageService(storageService); // Set in the store for hooks to use
        console.log('✅ [Firebase Init] Storage service set successfully');
        setIsInitializingFirebase(false);

        // Initialize Firebase Auth (skip if using external Firebase)
        if (!externalProps?.firebaseConfig) {
          try {
            const user = await initializeFirebaseAuth();
            if (user) {
          // console.log('✅ Firebase auth ready with user:', user.uid);
              setFirebaseAuthReady(true);
            } else {
          // console.log('⚠️ Firebase auth initialized but no user authenticated yet');
              // Still set as ready since auth system is initialized
              setFirebaseAuthReady(true);
            }
          } catch (authError) {
          // console.log('Firebase auth error (non-critical):', authError);
            // Continue without auth - some features may not work
            setFirebaseAuthReady(true); // Set as ready to not block other operations
          }
        } else {
          // External Firebase doesn't use our auth system
          setFirebaseAuthReady(true);
        }
        
        setFirebaseReady(true);
      } catch (error) {
        setIsInitializingFirebase(false);
        if (externalProps?.callbacks?.onError) {
          externalProps.callbacks.onError(error as Error);
        }
      }
    };
    
    initFirebase();
    
    // Cleanup on unmount
    return () => {
      if (firebaseService && externalProps?.firebaseConfig) {
        firebaseService.cleanup();
      }
    };
  }, [externalProps]);
  
  // Listen for Firebase auth state changes
  useEffect(() => {
    if (!externalProps?.firebaseConfig && firebaseReady) {
      import('../../../lib/firebase/authService').then(({ onFirebaseAuthStateChanged }) => {
        const unsubscribe = onFirebaseAuthStateChanged((user) => {
          if (user) {
          // console.log('🔥 Firebase auth state changed - user authenticated:', user.uid);
            setFirebaseAuthReady(true);
          }
        });
        
        return unsubscribe;
      });
    }
  }, [firebaseReady, externalProps?.firebaseConfig]);

  /**
   * Restore test state from Firebase metadata and answers
   * This is called by both normal mode and WebView mode after questions are loaded
   */
  const restoreTestState = useCallback(async (
    testId: string,
    questions: QBTestQuestionEto[],
    studyTaskIdParam?: string
  ): Promise<number> => {
    console.log('🔄 [restoreTestState] Starting restore process:', {
      testId,
      questionsCount: questions.length,
      studyTaskId: studyTaskIdParam,
      userId: userInfo?.id,
      tenantId,
      isWebViewMode
    });

    let initialQuestionIndex = 0;
    let firebaseAnswers: Record<string, OpticalAnswer> = {};

    // 1. Check for existing metadata
    let existingMetadata: any = null;
    const currentStorageService = useWhiteboardStore.getState().storageService;

    if (currentStorageService && userInfo?.id && tenantId) {
      try {
        const { TestMetadataService } = await import('../../../lib/firebase/testMetadataService');
        const effectiveTenantId = isWebViewMode ? 50000 : tenantId;

        console.log('🔍 [restoreTestState] Reading metadata:', {
          documentKey: `${effectiveTenantId}_${testId}`,
          userId: userInfo.id,
          tenantId: effectiveTenantId
        });

        existingMetadata = await TestMetadataService.getTestMetadata(
          userInfo.id,
          effectiveTenantId,
          testId
        );

        if (existingMetadata?.lastActiveQuestionId) {
          console.log('✅ [restoreTestState] Found existing metadata:', {
            lastActiveQuestionId: existingMetadata.lastActiveQuestionId,
            lastActiveQuestionIndex: existingMetadata.lastActiveQuestionIndex
          });

          // Find the index of last active question
          const lastQuestionIndex = questions.findIndex(
            q => q.questionId === existingMetadata.lastActiveQuestionId
          );
          if (lastQuestionIndex !== -1) {
            initialQuestionIndex = lastQuestionIndex;
            console.log('📍 [restoreTestState] Will resume at question index:', initialQuestionIndex);
          }
        } else {
          console.log('ℹ️ [restoreTestState] No existing metadata found');
        }
      } catch (error) {
        console.error('❌ [restoreTestState] Failed to read metadata:', error);
      }
    }

    // 2. Load answers from Firebase
    if (currentStorageService && userInfo?.id && tenantId) {
      try {
        const { AnswerDataService } = await import('../../../lib/firebase/answerDataService');
        const effectiveTenantId = isWebViewMode ? '50000' : tenantId.toString();

        console.log('📥 [restoreTestState] Loading answers from Firebase');

        const testAnswers = await AnswerDataService.getTestAnswers(
          testId,
          userInfo.id.toString(),
          effectiveTenantId,
          studyTaskIdParam || undefined
        );

        // Convert Firebase answers to optical answers format
        testAnswers.forEach(answer => {
          if (answer.studentAnswer && answer.questionId) {
            // Store ALL answers including '0' (unanswered but visited questions)
            // The optical UI components handle '0' appropriately (don't show selection)
            firebaseAnswers[answer.questionId] = answer.studentAnswer as OpticalAnswer;
          }
        });

        console.log('✅ [restoreTestState] Loaded answers:', Object.keys(firebaseAnswers).length);
        console.log('📊 [restoreTestState] Total documents from Firebase:', testAnswers.length);
        console.log('📊 [restoreTestState] Answers with value "0":', testAnswers.filter(a => a.studentAnswer === '0').length);
      } catch (error) {
        console.error('❌ [restoreTestState] Failed to load answers:', error);
      }
    }

    // 3. Load previous session if exists
    try {
      const previousSession = await loadPreviousSession();
      if (previousSession) {
        console.log('📂 [restoreTestState] Found previous session');

        // Restore session state (previous session takes priority over metadata)
        const resumeIndex = previousSession.lastQuestion?.questionIndex || initialQuestionIndex;
        initialQuestionIndex = resumeIndex;

        // Restore answers from session
        if (previousSession.questionsData) {
          const answersToRestore: Record<string, OpticalAnswer> = {};
          Object.entries(previousSession.questionsData).forEach(([qId, qData]) => {
            if (qData.opticalChoice && qData.opticalChoice !== '0') {
              answersToRestore[qId] = qData.opticalChoice as OpticalAnswer;
            }
          });

          // Merge session answers with Firebase answers (session takes priority)
          firebaseAnswers = { ...firebaseAnswers, ...answersToRestore };
          console.log('✅ [restoreTestState] Merged session answers, total:', Object.keys(firebaseAnswers).length);
        }
      }
    } catch (error) {
      console.error('❌ [restoreTestState] Failed to load session:', error);
    }

    // 4. Set optical answers if any
    if (Object.keys(firebaseAnswers).length > 0) {
      console.log('📝 [restoreTestState] Setting optical answers:', Object.keys(firebaseAnswers).length);
      console.log('📝 [restoreTestState] Answer keys:', Object.keys(firebaseAnswers).slice(0, 5).map(k => k.substring(0, 8) + '...'));
      console.log('📝 [restoreTestState] Answer values:', Object.values(firebaseAnswers).slice(0, 5));

      // Set to useTestStore (this is the source of truth for WhiteboardProvider context)
      const { useTestStore } = await import('../../../store/useTestStore');
      useTestStore.getState().setOpticalAnswers(firebaseAnswers);

      // Verify the answers were set
      const verifyAnswers = useTestStore.getState().opticalAnswers;
      console.log('✅ [restoreTestState] Verified optical answers in store:', Object.keys(verifyAnswers).length);
      console.log('📊 [restoreTestState] Sample answers:', Object.entries(verifyAnswers).slice(0, 3).map(([k, v]) => `${k.substring(0, 8)}...: ${v}`));
    } else {
      console.log('⚠️ [restoreTestState] No answers to restore (firebaseAnswers is empty)');
    }

    console.log('✅ [restoreTestState] Restore complete, initial index:', initialQuestionIndex);
    return initialQuestionIndex;
  }, [userInfo, tenantId, isWebViewMode, loadPreviousSession]);

  // Load test data
  const loadTest = useCallback(async (testId: string) => {
    try {
      setIsLoadingTest(true);
      setError(null);

      // Check if this is a blank whiteboard
      const isBlankWhiteboard = !testId || testId === BLANK_WHITEBOARD_TEST_ID;

      // Clear caches when starting a new test
          // console.log('🧹 Clearing caches for new test:', testId);
      
      // Get current answers to see if they belong to a different test
      const currentAnswers = opticalAnswers;
      const hasAnswersFromDifferentTest = Object.keys(currentAnswers).length > 0;
      
      if (hasAnswersFromDifferentTest) {
          // console.log('🔄 Clearing answers from previous test');
        clearAnswers(); // Clear old test answers
      }
      
      clearStrokes(); 
      setQuestionDataCache(new Map());
      
      // For blank whiteboard, use fixed values
      if (isBlankWhiteboard) {
        setCurrentTestId(BLANK_WHITEBOARD_TEST_ID);
        setStudyTaskId(BLANK_WHITEBOARD_TEST_ID);
        setTestTitle('Boş Tahta');
        setTotalTime(undefined);
        
        // Create a fake question for blank whiteboard
        const blankQuestion: QBTestQuestionEto = {
          questionId: BLANK_WHITEBOARD_QUESTION_ID,
          questionNumber: 1,
          imageUrl: '', // No image for blank whiteboard
          questionOrder: 1
        };
        
        setQuestions([blankQuestion]);
        setGroups([]);
        setCurrentQuestionIndex(0);
        setCurrentQuestionId(BLANK_WHITEBOARD_QUESTION_ID);
        setIsLoadingTest(false);
        return;
      }

      // Load test data
      const testData = await loadTestData(testId);
      
     
      
      // Set test info if available
      if (testData) {
        const timeInSeconds = (testData as any).time ? (testData as any).time * 60 : undefined;
        setTotalTime(timeInSeconds);
        setTestTitle((testData as any).name || '');
        
        // Build and store UI config based on test data
        const uiConfig = buildUIConfigFromTest(testData, userInfo, tenantInfo);
        setTestMetadata(testId, {
          testType: testData.qbTest?.examType,
          uiConfig
        });
      }
      setCurrentTestId(testId);
      
      // Determine study task ID for Firebase integration
      let determinedStudyTaskId: string;
      if (externalProps?.studyTask?.id) {
        determinedStudyTaskId = externalProps.studyTask.id;
        setStudyTaskId(determinedStudyTaskId);
          // console.log('📝 Set studyTaskId from externalProps:', externalProps.studyTask.id);
      } else if (externalProps?.testConfig?.studyTask?.id) {
        determinedStudyTaskId = externalProps.testConfig.studyTask.id;
        setStudyTaskId(determinedStudyTaskId);
          // console.log('📝 Set studyTaskId from testConfig:', externalProps.testConfig.studyTask.id);
      } else {
        determinedStudyTaskId = testId;
        setStudyTaskId(determinedStudyTaskId);
          // console.log('📝 Set studyTaskId to testId:', testId);
      }

      // Load questions
      const questionsData = await loadQuestionsData(testId);     
      
      // Check if no questions but we have metadata (indicates data exists but questions not loaded)
      if (!questionsData.questions || questionsData.questions.length === 0) {
        // Different error handling for WebView mode
        if (externalProps?.webViewMode) {
          // In WebView mode, trigger loading error state
          setWebViewLoadingError(true);
          setError(null);
          setIsLoadingTest(false);
          
          // Send not loaded message to return to app
          const { WebViewMessageService } = await import('../../../integrations/webview/services/messageService');
          const { WebViewMessageType } = await import('../../../integrations/webview/types');
          const messageService = WebViewMessageService.getInstance();
          messageService.send(WebViewMessageType.TEST_NOT_LOADED, {
            error: 'Test loading failed - no questions found',
            testId,
            timestamp: Date.now()
          });
          return;
        }
        
        // Normal mode error handling
        setError('Bu test için soru bulunamadı.');
        setIsLoadingTest(false);
        return;
      }
      
      setQuestions(questionsData.questions || []);
      setGroups(questionsData.groups || []);
      
      // ✅ OPTIMIZATION: Aggressive image preloading (first 5 questions)
      const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
      if (questionsData.questions && !externalProps) {
        const preloadCount = Math.min(5, questionsData.questions.length);
        console.log(`🖼️ Preloading ${preloadCount} question images...`);

        // Preload images in parallel
        const preloadPromises = questionsData.questions
          .slice(0, preloadCount)
          .map((question, index) => {
            return new Promise<void>((resolve) => {
              if (question?.imageUrl) {
                const img = new Image();
                img.onload = () => {
                  console.log(`✅ Preloaded image ${index + 1}/${preloadCount}`);
                  resolve();
                };
                img.onerror = () => {
                  console.warn(`⚠️ Failed to preload image ${index + 1}`);
                  resolve(); // Don't block on errors
                };
                // Add cache buster for first image only
                const cacheBuster = index === 0 ? `${question.imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : '';
                img.src = `${question.imageUrl}${cacheBuster}`;
              } else {
                resolve();
              }
            });
          });

        // Start preloading (don't await - run in background)
        Promise.all(preloadPromises).then(() => {
          console.log('✅ All images preloaded');
        });
      }
      
      // Initialize questionDataCache for all questions
      const newCache = new Map();

      // Load all questions data in background - OPTIMIZED WITH PARALLEL LOADING
      const loadRemainingQuestionsData = async () => {
        console.time('⚡ Parallel question data loading');

        // Get current answers from store to check which questions have answers
        const currentAnswers = useTestStore.getState().opticalAnswers;
        console.log('📊 [loadRemainingQuestionsData] Current answers in store:', Object.keys(currentAnswers).length);
        console.log('📊 [loadRemainingQuestionsData] Sample answers:', Object.entries(currentAnswers).slice(0, 5).map(([k, v]) => `${k.substring(0, 8)}...: ${v}`));

        // ✅ OPTIMIZATION: Parallel loading with Promise.allSettled
        const loadPromises = (questionsData.questions || []).map(async (question) => {
          const questionId = getStandardQuestionId(question);
          if (!questionId) return null;

          // Load question data for ALL questions (not just those with answers)
          // This ensures optical answers are properly restored even if they were saved after metadata
          const hasAnswer = currentAnswers[questionId] !== undefined;

          // Always load question data to ensure proper restore
          try {
            // Check if data exists in localStorage or Firebase
            const existingData = await loadQuestionDataHook(questionId);
            if (existingData) {
              return { questionId, data: existingData };
            } else {
              // Create initial question data with answer if available
              return {
                questionId,
                data: {
                  questionId,
                  userId: userInfo?.id || 0,
                  tenantId: tenantId || 0,
                  testId: testId,
                  selectionHistory: '',
                  trajectory: [],
                  totalTime: 0,
                  updatedAt: Date.now(),
                  opticalChoice: currentAnswers[questionId] // Will be undefined if no answer
                }
              };
            }
          } catch (error) {
            console.warn(`Failed to load question ${questionId}:`, error);
            return null;
          }
        });

        // Wait for all loads to complete (or fail)
        const results = await Promise.allSettled(loadPromises);

        // Update cache with all loaded data
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            newCache.set(result.value.questionId, result.value.data);
          }
        });

        setQuestionDataCache(newCache);

        console.timeEnd('⚡ Parallel question data loading');
        console.log(`✅ Loaded ${newCache.size} questions in parallel`);
      };
      
      // Update the cache in store
      setQuestionDataCache(newCache);

      // Call restoreTestState to get initial question index and load answers
      console.log('🔄 [loadTest] Calling restoreTestState...');
      const initialQuestionIndex = await restoreTestState(
        testId,
        questionsData.questions || [],
        determinedStudyTaskId
      );
      console.log('✅ [loadTest] restoreTestState completed, initial index:', initialQuestionIndex);

      // NOW that restore is complete, load question data in background
      // This ensures all Firebase answers are in the store before we check hasAnswer
      console.log('🔄 [loadTest] Starting background question data loading...');
      loadRemainingQuestionsData();

      // restoreTestState already handled metadata reading and answer loading
      // Now just create new metadata if this is first time (restoreTestState returns 0 for new tests)

      // Calculate used time from previous sessions
      if (!externalProps && totalTime && userInfo?.id && tenantId) {
        // Calculate in background without blocking UI
        setTimeout(async () => {
          try {
            const { AnswerDataService } = await import('../../../lib/firebase/answerDataService');
            // In WebView mode, always use tenantId 50000
            const effectiveTenantId = isWebViewMode ? '50000' : tenantId.toString();
            
            const answers = await Promise.race([
              AnswerDataService.getTestAnswers(
                testId, 
                userInfo.id.toString(), 
                effectiveTenantId,
                studyTaskId || undefined // Pass studyTaskId for WebView mode
              ),
              new Promise<any[]>((resolve) => setTimeout(() => resolve([]), 3000)) // 3s timeout
            ]);
            
            let totalUsedTime = 0;
            if (answers && answers.length > 0) {
              answers.forEach(answer => {
                if (answer.intervals) {
                  try {
                    const intervals = JSON.parse(answer.intervals);
                    const questionTime = intervals.reduce((sum: number, interval: any) => 
                      sum + (interval.duration || 0), 0
                    );
                    totalUsedTime += questionTime;
                  } catch (e) {
          // console.log('Failed to parse intervals');
                  }
                }
              });
              
              // Update local storage with calculated time
              localStorage.setItem(`test_resume_${testId}`, JSON.stringify({
                lastActiveQuestionId: questionsData.questions?.[initialQuestionIndex]?.questionId,
                usedTime: totalUsedTime,
                timestamp: Date.now()
              }));
            }
          } catch (error) {
          // console.log('Failed to calculate used time from Firebase');
          }
        }, 100);
      }
      
      // restoreTestState already loaded answers and set initialQuestionIndex
      // Just set the current question index
      setCurrentQuestionIndex(initialQuestionIndex);
    } catch (err) {
      // Different error handling for WebView mode
      if (externalProps?.webViewMode) {
        setWebViewLoadingError(true);
        setError(null);
        
        // Send not loaded message to return to app
        const { WebViewMessageService } = await import('../../../integrations/webview/services/messageService');
        const { WebViewMessageType } = await import('../../../integrations/webview/types');
        const messageService = WebViewMessageService.getInstance();
        messageService.send(WebViewMessageType.TEST_NOT_LOADED, {
          error: err instanceof Error ? err.message : 'Failed to load test',
          testId,
          timestamp: Date.now()
        });
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load test');
      }
    } finally {
      setIsLoadingTest(false);
    }
  }, [loadTestData, loadQuestionsData, externalProps, setCurrentTestId, setStudyTaskId,
      setQuestions, setGroups, setTestTitle, setTotalTime, setCurrentQuestionIndex,
      setOpticalAnswers, restoreTestState]);

  // Set studyTaskId from external props on mount
  useEffect(() => {
    if (externalProps?.studyTask?.id) {
      setStudyTaskId(externalProps.studyTask.id);
          // console.log('📝 Set studyTaskId from externalProps on mount:', externalProps.studyTask.id);
    }
  }, [externalProps]);

  // Track question index changes and update Firebase metadata
  useEffect(() => {
    console.log('🔍 [Metadata Update Effect] Triggered with:', {
      hasCurrentQbQuestion: !!currentQbQuestion,
      questionId: currentQbQuestion?.questionId,
      currentQuestionIndex,
      userId: userInfo?.id,
      tenantId,
      testId: qbTestId,
      hasExternalProps: !!externalProps,
      isInitialLoad
    });

    // Skip if no question or missing required data
    if (!currentQbQuestion || !userInfo?.id || !tenantId || !qbTestId) {
      console.log('❌ [Metadata Update Effect] Skipping - missing data:', {
        hasQuestion: !!currentQbQuestion,
        hasUserId: !!userInfo?.id,
        hasTenantId: !!tenantId,
        hasTestId: !!qbTestId
      });
      return;
    }

    // In external/WebView mode, only skip if storageService is not configured
    // (some external modes may still use Firebase for persistence)
    const hasStorageService = useWhiteboardStore.getState().storageService;
    if (externalProps && !hasStorageService) {
      console.log('❌ [Metadata Update Effect] Skipping - external props mode without Firebase');
      return;
    }

    // Don't update on initial load (handled by loadTest)
    if (currentQuestionIndex === -1) {
      console.log('❌ [Metadata Update Effect] Skipping - initial load (index = -1)');
      return;
    }

    // Skip updates during initial load phase
    if (isInitialLoad) {
      console.log('❌ [Metadata Update Effect] Skipping - initial load phase');
      return;
    }

    console.log('✅ [Metadata Update Effect] Proceeding with update for question:', currentQbQuestion.questionId);

    // Update Firebase metadata when question changes
    const updateMetadata = async () => {
      try {
        const { TestMetadataService } = await import('../../../lib/firebase/testMetadataService');
        const effectiveTenantId = isWebViewMode ? 50000 : tenantId;
        const currentStudyTaskId = studyTaskId || qbTestId;

        const updateData = {
          lastActiveQuestionId: currentQbQuestion.questionId,
          lastActiveQuestionIndex: currentQuestionIndex,
          tenantId: effectiveTenantId,
          userId: userInfo.id,
          testId: qbTestId,
          studyTaskId: currentStudyTaskId,
          defaultAnswerType: 2,
          lastUpdateTime: Date.now(),
          answeredQuestions: Object.keys(opticalAnswers).length,
          totalQuestions: testQuestions.length,
          status: 'in_progress' as const
        };

        console.log('📤 [Metadata Update] Sending to Firebase:', updateData);

        await TestMetadataService.updateTestMetadata(
          userInfo.id,
          effectiveTenantId,
          qbTestId,
          updateData
        );

        console.log('✅ [Metadata Update] Successfully updated Firebase metadata');
      } catch (error) {
        console.error('❌ [Metadata Update] Error updating test metadata:', error);
      }
    };

    updateMetadata();
  }, [currentQuestionIndex, currentQbQuestion?.questionId, opticalAnswers, userInfo?.id, tenantId, qbTestId, studyTaskId, isWebViewMode, testQuestions.length, externalProps, isInitialLoad]); // Track all dependencies

  // Mark initial load as complete after test is loaded and questions are set
  useEffect(() => {
    // In WebView mode, wait for questions to be loaded by wrapper
    if (externalProps && isWebViewMode) {
      if (testQuestions.length > 0) {
        // Longer delay for WebView to ensure restore is complete
        const timer = setTimeout(() => {
          console.log('✅ [WebView] Initial load complete, enabling metadata updates');
          setIsInitialLoad(false);
        }, 1000);
        return () => clearTimeout(timer);
      }
    } else {
      // Normal mode
      if (!isLoadingTest && testQuestions.length > 0 && currentQuestionIndex !== -1) {
        const timer = setTimeout(() => {
          console.log('✅ [Normal] Initial load complete, enabling metadata updates');
          setIsInitialLoad(false);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoadingTest, testQuestions.length, currentQuestionIndex, externalProps, isWebViewMode]);

  // WebView mode: Restore test state when questions are loaded
  const webViewLastTestIdRef = useRef<string | null>(null);
  useEffect(() => {
    // In WebView mode, use currentTestId from store (set by WebViewWhiteboardWrapper)
    const effectiveTestId = isWebViewMode ? currentTestId : qbTestId;

    console.log('🔍 [WebView Restore Effect] Conditions:', {
      externalProps: !!externalProps,
      isWebViewMode,
      questionsLength: testQuestions.length,
      qbTestId,
      currentTestId,
      effectiveTestId,
      isLoadingTest,
      isInitializingFirebase,
      lastTestId: webViewLastTestIdRef.current
    });

    if (isWebViewMode && testQuestions.length > 0 && effectiveTestId && !isLoadingTest && !isInitializingFirebase) {
      // Only restore once per test ID
      if (webViewLastTestIdRef.current !== effectiveTestId) {
        webViewLastTestIdRef.current = effectiveTestId;
        console.log('🔄 [WebView] Questions loaded, calling restoreTestState for testId:', effectiveTestId);

        const studyTaskId = externalProps?.studyTask?.id;
        restoreTestState(effectiveTestId, testQuestions, studyTaskId).then((initialIndex) => {
          console.log('✅ [WebView] Restore complete, setting question index to:', initialIndex);
          setCurrentQuestionIndex(initialIndex);
        }).catch((error) => {
          console.error('❌ [WebView] Restore failed:', error);
        });
      } else {
        console.log('⏭️ [WebView] Skipping restore - already restored for this testId');
      }
    } else {
      console.log('⏭️ [WebView] Skipping restore - conditions not met');
    }
  }, [externalProps, isWebViewMode, testQuestions.length, qbTestId, currentTestId, isLoadingTest, isInitializingFirebase, restoreTestState, setCurrentQuestionIndex]);

  // Load test on mount and when study task changes
  useEffect(() => {
    // In WebView mode with external props, data loading is handled by WebViewWhiteboardWrapper
    // WhiteboardProvider should not load test data in this case
    if (externalProps && isWebViewMode) {
          // console.log('📝 WhiteboardProvider - WebView mode with external props, data loading handled by WebViewWhiteboardWrapper');
      setIsLoadingTest(false);
      return;
    }

    const currentStrokes = useWhiteboardStore.getState().strokes;

    // For blank whiteboard (no qbTestId), still need to initialize
    if (!qbTestId) {
      if (isAuthenticated) {
        // Load blank whiteboard
        loadTest(BLANK_WHITEBOARD_TEST_ID);
      }
      return;
    }

    if (!isAuthenticated) return;

          // console.log('📝 WhiteboardProvider - Loading test, testId:', qbTestId);
    loadTest(qbTestId);
  }, [qbTestId, isAuthenticated, loadTest, externalProps, isWebViewMode]);

  // Navigation callbacks with save logic
  const navigateToQuestion = useCallback(async (index: number) => {
   
    
    // Import getStandardQuestionId at the beginning
    const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
    
    // Save current question data and end trajectory before changing
    if (saveCurrentQuestionDataRef.current && currentQbQuestion) {
    
      try {
        // Save pending optical answers first
        await savePendingOpticalAnswers();
        // End the current trajectory session
        const currentQuestionId = getStandardQuestionId(currentQbQuestion);
        
        if (currentQuestionId && questionStartTimeRef.current && currentQuestionIdRef.current === currentQuestionId) {
          // Calculate duration
          const endTime = Date.now();
          const duration = (endTime - questionStartTimeRef.current) / 1000; // in seconds
          
          // Get from cache instead of loading
          const currentCache = useWhiteboardStore.getState().questionDataCache;
          const existingData = currentCache.get(currentQuestionId);
          
          if (existingData && existingData.trajectory) {
            const trajectory = [...existingData.trajectory];
            
            // Import interval utilities
            const { closeActiveSession } = await import('../../../utils/intervalUtils');
            
            // Close the active session when navigating away
            closeActiveSession(trajectory);
            
            let trajectoryUpdated = false;
            if (trajectory.length > 0) {
              const lastSession = trajectory[trajectory.length - 1];
              if (lastSession.duration !== undefined) {
                trajectoryUpdated = true;
          // console.log('📝 Closed interval on navigation for question:', currentQuestionId, 'Duration:', lastSession.duration.toFixed(2), 'seconds');
              }
            }
            
            // Update cache immediately if trajectory changed
            if (trajectoryUpdated) {
              // Calculate total duration from all sessions
              const totalDuration = trajectory.reduce((total, session) => {
                return total + (session.duration || 0);
              }, 0);
              
          // console.log('⏱️ Total duration for question:', currentQuestionId, 'is', totalDuration, 'seconds');
              
              const updatedQuestionData: QuestionData = {
                ...existingData,
                trajectory: trajectory,
                totalTime: totalDuration,
                updatedAt: Date.now()
              };
              
              const newCache = new Map(currentCache);
              newCache.set(currentQuestionId, updatedQuestionData);
              useWhiteboardStore.getState().setQuestionDataCache(newCache);
              
              // In external mode, we need to save to Firebase
              if (externalProps && questionStorageService) {
                try {
                  await questionStorageService.saveQuestionData(updatedQuestionData);
          // console.log('✅ Saved trajectory end time to Firebase');
                } catch (error) {
                  console.error('Error saving trajectory end time:', error);
                }
              }
            }
          }
          
          // Clear timer refs
          questionStartTimeRef.current = null;
          currentQuestionIdRef.current = null;
          
          // Save any pending data
          if (saveCurrentQuestionDataRef.current) {
            await saveCurrentQuestionDataRef.current();
          }
        }
      
      } catch (error) {
      }
    }
    
    // Update test metadata with new active question
    const newQuestion = testQuestions[index];
    if (newQuestion && !externalProps && userInfo?.id && tenantId && qbTestId) {
      // Save to localStorage immediately for offline support
      const resumeData = {
        lastActiveQuestionId: newQuestion.questionId,
        lastActiveQuestionIndex: index, // Add question index
        usedTime: previousSessionsTime + (Date.now() - (questionStartTimeRef.current || Date.now())),
        timestamp: Date.now()
      };
      localStorage.setItem(`test_resume_${qbTestId}`, JSON.stringify(resumeData));

      // Only update Firebase if not in initial load phase
      if (!isInitialLoad) {
        // Then update Firebase in background
        try {
          const { TestMetadataService } = await import('../../../lib/firebase/testMetadataService');
          // Use consistent tenant ID for WebView mode
          const effectiveTenantId = isWebViewMode ? 50000 : tenantId;

          // Get the current studyTaskId from store
          const currentStudyTaskId = studyTaskId || qbTestId;

          const navigateUpdateData = {
            lastActiveQuestionId: newQuestion.questionId,
            lastActiveQuestionIndex: index,
            tenantId: effectiveTenantId,
            userId: userInfo.id,
            testId: qbTestId,
            studyTaskId: currentStudyTaskId,
            defaultAnswerType: 2,
            lastUpdateTime: Date.now(),
            answeredQuestions: Object.keys(opticalAnswers).length,
            totalQuestions: testQuestions.length,
            status: 'in_progress' as const
          };

          console.log('📤 [navigateToQuestion] Updating Firebase metadata:', {
            documentKey: `${effectiveTenantId}_${qbTestId}`,
            updateData: navigateUpdateData
          });

        await TestMetadataService.updateTestMetadata(
          userInfo.id,
          effectiveTenantId,
          qbTestId,
          navigateUpdateData
        ).then(() => {
          console.log('✅ [navigateToQuestion] Metadata updated successfully');
        }).catch(err => {
          console.error('❌ [navigateToQuestion] Failed to update metadata:', err);
        });
        } catch (error) {
          console.error('Error updating test metadata:', error);
        }
      } else {
        console.log('⏭️ [navigateToQuestion] Skipping Firebase update - initial load phase');
      }
    }

    // Small delay to ensure save completes
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Update index
    setCurrentQuestionIndex(index);

    // Update currentQuestionId in whiteboard store
    if (newQuestion) {
      const newQuestionId = getStandardQuestionId(newQuestion);
      useWhiteboardStore.getState().setCurrentQuestionId(newQuestionId);
    }

    // ✅ OPTIMIZATION: Preload next 3 questions' images
    const nextQuestions = testQuestions.slice(index + 1, index + 4);
    if (nextQuestions.length > 0) {
      // Run in background, don't await
      Promise.all(
        nextQuestions.map((q) => {
          return new Promise<void>((resolve) => {
            if (q?.imageUrl) {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              img.src = q.imageUrl;
            } else {
              resolve();
            }
          });
        })
      ).catch(() => {
        // Silently fail - preloading is best effort
      });
    }
    
    // ✅ OPTIMIZATION: Batch canvas operations for better performance
    if (fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;

      // Suspend rendering during batch operations
      canvas.renderOnAddRemove = false;

      // Get all objects before filtering
      const allObjects = canvas.getObjects();

      // Remove ALL user drawing objects (paths) - we'll re-add the ones for the new question
      const drawingObjects = allObjects.filter((obj: any) => {
        return obj.isUserDrawing || (obj.type === 'path' && !obj.name?.includes('question') && !obj.name?.includes('optical'));
      });

      // Batch remove
      drawingObjects.forEach((obj: any) => canvas.remove(obj));

      // DO NOT clear strokes from store - we need to keep all strokes for all questions
      // Only the canvas objects should be cleared, not the store data
      const currentStrokes = useWhiteboardStore.getState().strokes;

      // Resume rendering and do single render call
      canvas.renderOnAddRemove = true;
      canvas.requestRenderAll();
    }
    
    // Another small delay before loading new question
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Start new trajectory session for the new question
    if (newQuestion) {
      const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
      const newQuestionId = getStandardQuestionId(newQuestion);
      
      if (newQuestionId) {
        const existingData = await loadQuestionDataHook(newQuestionId);
        const trajectory = existingData?.trajectory || [];
        
        // Only add new session if there isn't already an active one
        const hasActiveSession = trajectory.length > 0 && !trajectory[trajectory.length - 1].endDate;
        
        if (!hasActiveSession) {
          // Add new trajectory session
          trajectory.push({
            startDate: Date.now(),
            endDate: undefined,
            duration: undefined
          });
          
       
          
          // Update question data cache immediately
          const updatedQuestionData: QuestionData = {
            questionId: newQuestionId,
            userId: userInfo?.id || 0,
            tenantId: tenantId || 0,
            testId: qbTestId || '',
            ...existingData,
            trajectory: trajectory,
            selectionHistory: existingData?.selectionHistory || '',
            totalTime: existingData?.totalTime || 0,
            updatedAt: Date.now()
          };
          
          // Update cache first
          const currentCache = useWhiteboardStore.getState().questionDataCache;
          const newCache = new Map(currentCache);
          newCache.set(newQuestionId, updatedQuestionData);
          useWhiteboardStore.getState().setQuestionDataCache(newCache);
          
          // Then save to storage
          await saveQuestionDataHook(newQuestionId, updatedQuestionData);
        } else {
        }
      }
    }
  }, [currentQbQuestion, setCurrentQuestionIndex, testQuestions, loadQuestionDataHook, saveQuestionDataHook, 
      savePendingOpticalAnswers, externalProps, userInfo, tenantId, qbTestId, questionStorageService]);

  const goToNextQuestion = useCallback(async () => {
    if (currentQuestionIndex < testQuestions.length - 1) {
      await navigateToQuestion(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, testQuestions.length, navigateToQuestion]);

  const goToPreviousQuestion = useCallback(async () => {
    if (currentQuestionIndex > 0) {
      await navigateToQuestion(currentQuestionIndex - 1);
    }
  }, [currentQuestionIndex, navigateToQuestion]);

  // Save current question data
  const saveQuestionData = useCallback(async () => {
    if (!currentQbQuestion || !fabricCanvasRef.current) return;
    
    // Use standardized question ID
    const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
    const questionId = getStandardQuestionId(currentQbQuestion);
    
    if (!questionId) {
      return;
    }
    
  

    // Filter strokes for current question only
    const currentQuestionStrokes = strokes.filter(s => s.questionId === questionId);

    // Import interval utilities
    const { closeActiveSession } = await import('../../../utils/intervalUtils');
    
    // Get or create trajectory for this question
    const existingData = await loadQuestionDataHook(questionId);
    const trajectory = existingData?.trajectory || [];
    
    // Close the active session (if any)
    closeActiveSession(trajectory);
    
    if (trajectory.length > 0) {
      const lastSession = trajectory[trajectory.length - 1];
      if (lastSession.duration !== undefined) {
          // console.log('📝 Closed interval for question:', questionId, 'Duration:', lastSession.duration.toFixed(2), 'seconds');
      }
    }
    
    // Calculate total time
    const totalTime = trajectory.reduce((sum, t) => sum + (t.duration || 0), 0);
    
    // Get selection history from question data cache (which is updated by setOpticalAnswer)
    const questionDataFromCache = questionDataCache.get(questionId);
    const selectionHistory = questionDataFromCache?.selectionHistory || existingData?.selectionHistory || '';
    
    // Prepare question info
    const questionInfo = {
      imageUrl: currentQbQuestion.imageUrl || undefined,
      parentQuestionImageUrl: currentQbQuestion.parentQuestionImageUrl || undefined,
      answerType: currentQbQuestion.answerType || (testQuestions[0]?.answerType || 2), // Default from test
      href: currentQbQuestion.href || undefined,
      isCommonText: currentQbQuestion.isCommonText || false
    };

    // Prepare the data to save
    const dataToSave: Partial<QuestionData> = {
      drawingData: {
        strokes: currentQuestionStrokes,
        fabricObjects: []
      },
      opticalChoice: opticalAnswers[questionId] || existingData?.opticalChoice,
      eliminatedChoices: eliminatedChoicesString[questionId] || existingData?.eliminatedChoices,
      selectionHistory: selectionHistory,
      trajectory: trajectory,
      totalTime: totalTime,
      questionInfo: questionInfo,
      questionMetadata: {
        scale: 1,
        left: 0,
        top: 0
      }
    };
    
    // Generate content hash
    const { generateContentHash } = await import('../../../utils/contentHash');
    const contentHash = await generateContentHash(dataToSave);
    
    // Check if content has changed
    if (existingData?.contentHash === contentHash) {
     
      return;
    }
    
    // Save using the hook with all fields including hash
    await saveQuestionDataHook(questionId, {
      ...dataToSave,
      contentHash
    });

    // Save to Firebase if in optical mode
    if (firebaseReady && !externalProps && opticalAnswers[questionId]) {
      await saveAnswer(questionId, opticalAnswers[questionId]);
    }
  }, [currentQbQuestion, strokes, opticalAnswers, eliminatedChoicesString, questionDataCache,
      saveQuestionDataHook, loadQuestionDataHook, firebaseReady, externalProps, saveAnswer, testQuestions]);

  // Load question data
  const loadQuestionData = useCallback(async () => {
    if (!currentQbQuestion) return null;
    
    // Use standardized question ID
    const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
    const questionId = getStandardQuestionId(currentQbQuestion);
    
    if (!questionId) {
      return null;
    }
    
   

    const data = await loadQuestionDataHook(questionId);
    
    // Update question data cache with loaded data or initialize empty entry
    const newCache = new Map(questionDataCache);
    
    // Get existing or loaded data
    const questionData = data || questionDataCache.get(questionId) || {
      tenantId: tenantId || 0,
      userId: userInfo?.id || 0,
      testId: qbTestId || '',
      questionId: questionId,
      updatedAt: Date.now(),
      selectionHistory: '',
      eliminatedChoices: '',
      trajectory: [],
      totalTime: 0
    };
    
    // Import interval utilities
    const { startNewSession } = await import('../../../utils/intervalUtils');
    
    // Get or create trajectory array
    const trajectory = [...(questionData.trajectory || [])];
    
    // Always start a new session when loading a question
    const newSession = startNewSession(trajectory);
    
    // Update question data with new trajectory
    questionData.trajectory = trajectory;
    questionData.updatedAt = Date.now();
    
    // Update timer reference for the new session
    questionStartTimeRef.current = newSession.startDate;
    currentQuestionIdRef.current = questionId;
          // console.log('📝 Started new interval for question:', questionId, 'Total intervals:', trajectory.length, 'Session start:', new Date(newSession.startDate).toLocaleTimeString());
    
    newCache.set(questionId, questionData);
    setQuestionDataCache(newCache);
    
    if (data) {
      // Restore optical answer if exists
      if (data.opticalChoice && data.opticalChoice !== '0') {
       
        setOpticalAnswer(questionId, data.opticalChoice as 'A' | 'B' | 'C' | 'D' | 'E');
        
        // Force visual update after a short delay to ensure canvas is ready
        setTimeout(() => {
          if (fabricCanvasRef.current) {
            fabricCanvasRef.current.requestRenderAll();
          }
        }, 200);
      }
      
      // Restore eliminated choices if exists
      if (data.eliminatedChoices) {
        // Set eliminated choices by toggling each one
        const currentEliminated = eliminatedChoicesString[questionId] || '';
        const newEliminated = data.eliminatedChoices;
        
        // Remove all current eliminated
        currentEliminated.split('').forEach(choice => {
          toggleEliminatedChoice(questionId, choice);
        });
        
        // Add all new eliminated
        newEliminated.split('').forEach((choice: string) => {
          toggleEliminatedChoice(questionId, choice);
        });
      }
      
      // Restore drawing strokes if exists
      if (data.drawingData && data.drawingData.strokes && Array.isArray(data.drawingData.strokes)) {
       
        
        // Get the store state and methods
        const store = useWhiteboardStore.getState();
        const currentStrokes = store.strokes;
        
        // Clear existing strokes for this question from the store first
        const filteredStrokes = currentStrokes.filter(s => s.questionId !== questionId);
        
        // Add loaded strokes with proper questionId and filter out single-point strokes
        const loadedStrokes = data.drawingData.strokes
          .filter((stroke: any) => stroke.points && stroke.points.length > 1)
          .map((stroke: any) => ({
            ...stroke,
            questionId: questionId, // Ensure questionId is set
            id: stroke.id || `stroke-${Date.now()}-${Math.random()}` // Ensure unique ID
          }));
        
        // Update strokes in the store - replace strokes for this question
        useWhiteboardStore.setState({ 
          strokes: [...filteredStrokes, ...loadedStrokes] 
        });
        
       
        
        // Re-draw strokes on canvas if available
        if (fabricCanvasRef.current && loadedStrokes.length > 0) {
          const canvas = fabricCanvasRef.current;
          
          // Remove only strokes for this question from canvas
          const objectsToRemove = canvas.getObjects().filter((obj: any) => {
            return obj.isUserDrawing && obj.questionId === questionId;
          });
          
          objectsToRemove.forEach((obj: any) => canvas.remove(obj));
          
          // Add loaded strokes to canvas
          loadedStrokes.forEach((stroke: any) => {
            // Use drawing service or direct canvas manipulation to add strokes
            // This ensures proper rendering with all attributes
            if (stroke.points && stroke.points.length > 1) {
              const pathData = getPointsToSmoothPath(stroke.points);
              const path = new Path(pathData, {
                stroke: stroke.color,
                strokeWidth: stroke.width,
                fill: '',
                strokeLineCap: 'round',
                strokeLineJoin: 'round',
                selectable: false,
                evented: false,
                opacity: stroke.tool === 'highlighter' ? 0.5 : 1,
                globalCompositeOperation: stroke.tool === 'eraser' ? 'destination-out' : 'source-over'
              });
              
              // Set stroke metadata
              (path as any).strokeId = stroke.id;
              (path as any).id = stroke.id;
              (path as any).isUserDrawing = true;
              (path as any).questionId = questionId;
              
              canvas.add(path);
              // Path is already on top when added
            }
          });
          
          canvas.requestRenderAll();
        }
      }
    }
    
    return data;
  }, [currentQbQuestion, loadQuestionDataHook, setOpticalAnswer, 
      toggleEliminatedChoice, eliminatedChoicesString, questionDataCache, setQuestionDataCache,
      tenantId, userInfo?.id, qbTestId]);

  // Update ref when save function changes
  useEffect(() => {
    saveCurrentQuestionDataRef.current = saveQuestionData;
  }, [saveQuestionData]);

  // Optical answer actions
  const submitOpticalAnswer = useCallback((choice: 'A' | 'B' | 'C' | 'D' | 'E' | '') => {
   
    if (!currentQbQuestion?.questionId) {
      return;
    }
    
    // If empty string, clear the answer
    if (choice === '') {
      setOpticalAnswer(currentQbQuestion.questionId, null);
      return;
    }
    
    // This will update the selection history in useTestStore
    setOpticalAnswer(currentQbQuestion.questionId, choice);
    
    // Save to Firebase after a short delay
    setTimeout(async () => {
      await savePendingOpticalAnswers();
          // console.log('✅ Optical answer saved to Firebase');
    }, 500);
    
  }, [currentQbQuestion, setOpticalAnswer, savePendingOpticalAnswers]);

  const eliminateChoice = useCallback((choice: string) => {
    if (!currentQbQuestion?.questionId) return;
    
    toggleEliminatedChoice(currentQbQuestion.questionId, choice);
    
    // DO NOT save immediately - will save on question change or panel close
   
  }, [currentQbQuestion, toggleEliminatedChoice]);

  const clearAnswer = useCallback((questionId: string) => {
    setOpticalAnswer(questionId, null);
    
    // DO NOT save immediately - will save on question change or panel close
   
  }, [setOpticalAnswer]);
  
  // Handle test completion
  const handleTestComplete = useCallback(async () => {
    try {
      // Save pending optical answers before completing test
      await savePendingOpticalAnswers();
      
      // Save final session
      await handleTestCompleteSession();
      
      // Mark test as completed
      setTestCompleted(true);
      
      // Update test metadata as completed
      if (!externalProps && userInfo?.id && tenantId && qbTestId) {
        try {
          const { TestMetadataService } = await import('../../../lib/firebase/testMetadataService');
          // Use consistent tenant ID for WebView mode
          const effectiveTenantId = isWebViewMode ? 50000 : tenantId;
          const currentStudyTaskId = studyTaskId || qbTestId;

          await TestMetadataService.updateTestMetadata(
            userInfo.id,
            effectiveTenantId,
            qbTestId,
            {
              isCompleted: true,
              completedQuestions: testQuestions.map(q => q.questionId).filter((id): id is string => typeof id === 'string'),
              tenantId: effectiveTenantId, // Explicitly add tenantId
              userId: userInfo.id,
              testId: qbTestId,
              studyTaskId: currentStudyTaskId, // Add studyTaskId
              status: 'completed',
              lastUpdateTime: Date.now(),
              answeredQuestions: Object.keys(opticalAnswers).length,
              totalQuestions: testQuestions.length
            }
          );
        } catch (error) {
        }
      }
      
      // Submit bulk answers if in optical mode
      if (isOpticalMode && Object.keys(opticalAnswers).length > 0) {
        await submitBulkAnswers();
      }
      
      // Send WebView message with answer data if in WebView mode
      if (isWebViewMode) {
        const { WebViewMessageService } = await import('../../../integrations/webview/services/messageService');
        const { WebViewMessageType } = await import('../../../integrations/webview/types');
        const { getStandardQuestionId } = await import('../../../utils/questionIdUtils');
        
        const messageService = WebViewMessageService.getInstance();
        const whiteboardStore = useWhiteboardStore.getState();
        const testStore = useTestStore.getState();
        
        // First, end the current trajectory session if active (same logic as collectAnswerData)
        if (currentQbQuestion) {
          const currentQuestionId = getStandardQuestionId(currentQbQuestion);
          
          if (currentQuestionId) {
            const currentData = whiteboardStore.questionDataCache.get(currentQuestionId);
            if (currentData && currentData.trajectory && currentData.trajectory.length > 0) {
              const lastSession = currentData.trajectory[currentData.trajectory.length - 1];
              if (!lastSession.endDate) {
                const endTime = Date.now();
                const startTime = lastSession.startDate;
                const duration = (endTime - startTime) / 1000;
                
                lastSession.endDate = endTime;
                lastSession.duration = duration;
                
                // Update the cache with the closed session
                const newCache = new Map(whiteboardStore.questionDataCache);
                newCache.set(currentQuestionId, {
                  ...currentData,
                  trajectory: [...currentData.trajectory],
                  updatedAt: Date.now()
                });
                whiteboardStore.setQuestionDataCache(newCache);
                
          // console.log('📝 Ended active trajectory session before test completion:', currentQuestionId);
              }
            }
          }
        }
        
        // Collect answers in the final format
        const answers: any[] = [];
        
        Array.from(whiteboardStore.questionDataCache.entries()).forEach(([questionId, data]) => {
          // Skip if no answer selected
          if (!data.opticalChoice) {
            return;
          }
          
          // Calculate intervals and total duration
          let intervals: string = '';
          let totalDuration = 0;
          
          if (data.trajectory && data.trajectory.length > 0) {
            const formattedIntervals = data.trajectory.map((session) => {
              let duration = session.duration;
              let endTime = session.endDate;
              
              // For active sessions, calculate current duration
              if (!session.endDate && !session.duration) {
                const currentTime = Date.now();
                duration = (currentTime - session.startDate) / 1000;
                endTime = currentTime;
              } else if (!duration && session.endDate) {
                duration = (session.endDate - session.startDate) / 1000;
              }
              
              totalDuration += duration || 0;
              
              return {
                start: new Date(session.startDate).toISOString().replace('T', ' ').substring(0, 19),
                end: endTime ? new Date(endTime).toISOString().replace('T', ' ').substring(0, 19) : null,
                duration: Math.round((duration || 0) * 100) / 100
              };
            });
            
            intervals = JSON.stringify(formattedIntervals);
          }
          
          answers.push({
            questionId,
            studentAnswer: data.opticalChoice,
            trajectory: data.selectionHistory || '',
            eliminationTrajectory: data.eliminatedChoices || '',
            intervals: intervals,
            totalDuration: Math.round(totalDuration * 100) / 100,
            isSkipped: false,
            isEvaluate: true,
            processed: false,
            proficiencyInOutcome: 0
          });
        });
        
        const answerData = {
          // Basic info
          studyTaskId: externalProps?.studyTask?.id,
          userAccountId: externalProps?.authConfig?.userInfo?.id,
          testId: qbTestId || testStore.currentTestId,
          
          // Answers in final format
          answers: answers,
          
          // Optical answers (current selections) - for backward compatibility
          opticalAnswers,
          
          // Eliminated choices - for backward compatibility
          eliminatedChoices,
          
          // Question data with history - for backward compatibility
          questionData: Array.from(whiteboardStore.questionDataCache.entries()).map(([questionId, data]) => ({
            questionId,
            opticalChoice: data.opticalChoice,
            selectionHistory: data.selectionHistory,
            eliminatedChoices: data.eliminatedChoices,
            totalTime: data.totalTime,
            trajectory: data.trajectory,
            hasDrawings: !!(data.drawingData?.strokes?.length || data.drawingData?.fabricObjects?.length),
            // Add intervals field - format trajectory data as intervals JSON string
            intervals: data.trajectory && data.trajectory.length > 0 
              ? JSON.stringify(data.trajectory.map((session) => {
                  const endTime = session.endDate || Date.now();
                  
                  return {
                    start: new Date(session.startDate).toISOString(),
                    end: new Date(endTime).toISOString()
                  };
                }))
              : null
          })),
          
          // Test metadata
          totalQuestions: testQuestions.length,
          answeredQuestions: Object.keys(opticalAnswers).length,
          currentQuestionIndex,
          testTitle: testTitle || testStore.testTitle,
          
          // Session info
          sessionStartTime: testStore.sessionStartTime,
          sessionCount,
          isTestCompleted: true,
          
          // Additional metadata
          timestamp: Date.now()
        };
        
        messageService.send(WebViewMessageType.TEST_COMPLETED, answerData);
          // console.log('📱 Sent TEST_COMPLETED message to WebView');
        
        // Clean up test data after sending in WebView mode
        await cleanupWebViewTestData(qbTestId);
      } else {
        // Clean up test data in normal mode
        await cleanupTestData(qbTestId);
        
        // Navigate to library page in normal mode
        navigate('/library');
      }
    
    } catch (error) {
      console.error('Error in handleTestComplete:', error);
    }
  }, [handleTestCompleteSession, setTestCompleted, isOpticalMode, opticalAnswers, submitBulkAnswers, 
      externalProps, userInfo, tenantId, qbTestId, testQuestions, savePendingOpticalAnswers,
      isWebViewMode, currentQuestionIndex, testTitle, sessionCount, eliminatedChoices, navigate]);

  // Load device preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (userInfo?.id && tenantId) {
        await loadDevicePreferences();
      }
    };
    
    loadPreferences();
  }, [userInfo?.id, tenantId, loadDevicePreferences]);

  const value: WhiteboardContextType = {
    // Test data
    qbTestId,
    qbQuestions: testQuestions,
    qbGroups: testGroups,
    currentQuestionIndex,
    currentQbQuestion,
    testTitle,
    totalTime,
    isTestMode,
    isTestCompleted,
    
    // Optical answers
    opticalAnswers,
    eliminatedChoices,
    
    // Canvas state
    fabricCanvasRef,
    canvasReady,
    setCanvasReady,
    
    // Loading states
    isLoadingTest,
    isLoadingQuestions,
    error,
    webViewLoadingError,
    
    // Firebase
    firebaseReady,
    firebaseService,
    questionStorageService,
    
    // Modes
    isOpticalMode,
    isWebViewMode,
    showOpticalPanelState,
    
    // Recording
    isRecording,
    recordingEvents,
    
    // Session
    sessionCount,
    previousSessionsTime,
    
    // Actions - Navigation
    navigateToQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    setSelectedGroupIndex,
    
    // Actions - Test management
    loadTest,
    handleTestComplete,
    restoreTestState,
    
    // Actions - Question data
    saveQuestionData,
    loadQuestionData,
    syncToFirebase: syncToFirebaseHook,
    
    // Actions - Optical answers
    submitOpticalAnswer,
    eliminateChoice,
    clearAnswer,
    clearAllAnswers: clearAnswers,
    
    // Actions - UI
    setShowOpticalPanelState,
    
    // Answer data service methods
    saveAnswer,
    savePendingOpticalAnswers,
    submitBulkAnswers,
    markQuestionSkipped
  };

  return (
    <WhiteboardContext.Provider value={value}>
      {children}
    </WhiteboardContext.Provider>
  );
};